import { TCreateCompanyReqDto, TCreateCompanyResDto } from "@/api/services/admin/company/dtos/create-company.dto";
import { TDeleteCompanyResDto } from "@/api/services/admin/company/dtos/delete-company.dto";
import { TGetCompanyCountReqDto, TGetCompanyCountResDto } from "@/api/services/admin/company/dtos/get-company-count.dto";
import { TApiCompanyListPagReq, TGetCompanyListResDto } from "@/api/services/admin/company/dtos/get-company-list.dto";
import { TGetCompanyResDto } from "@/api/services/admin/company/dtos/get-company.dto";
import { TSwitchUserAsAdminResDto } from "@/api/services/admin/company/dtos/switch-user-as-admin.dto";
import { TUpdateCompanyReqDto, TUpdateCompanyResDto } from "@/api/services/admin/company/dtos/update-company.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { toNumber } from "@/utils/number";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

export const createCompanyApi = async (payload: TCreateCompanyReqDto): Promise<TCreateCompanyResDto> => {
  const rs = await clientApi.post<TCreateCompanyResDto>("/companies", payload);
  return rs.data;
};

export const fetchCompanyApi = async (companyId: string): Promise<TGetCompanyResDto> => {
  const rs = await clientApi.get<TGetCompanyResDto>(`/companies/${companyId}`);
  return rs.data;
};

export const useCompany = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchCompanyApi(id) : undefined),
    enabled: Boolean(id),
  });
};

export const fetchCompanyListApi = async (req: TApiCompanyListPagReq): Promise<TGetCompanyListResDto> => {
  const url = createUrlWithParams("/companies", req);
  const rs = await clientApi.get<TGetCompanyListResDto>(url);
  return rs.data;
};

export const useCompanyList = (req: TApiCompanyListPagReq) => {
  const params = {
    ...req,
    sorts: toNumber(req.sorts?.length) > 0 ? req.sorts : ["company.createdAt|DESC"],
  };

  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST, params],
    queryFn: () => fetchCompanyListApi(params),
  });
};

export const fetchCompanyCountApi = async (req?: TGetCompanyCountReqDto): Promise<TGetCompanyCountResDto> => {
  const url = createUrlWithParams("/companies/count", req);
  const rs = await clientApi.get<TGetCompanyCountResDto>(url);
  return rs.data;
};

export const useCompanyCount = (req?: TGetCompanyCountReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.COMPANY.FETCH_COUNT, req],
    queryFn: () => fetchCompanyCountApi(req),
  });
};

export const setCompanyAdminApi = async (companyId: string, userId: number): Promise<TSwitchUserAsAdminResDto> => {
  const rs = await clientApi.put<TSwitchUserAsAdminResDto>(`/companies/${companyId}/users/${userId}/set-as-admin`);
  return rs.data;
};

export const updateCompanyApi = async (companyId: string, payload: TUpdateCompanyReqDto): Promise<TUpdateCompanyResDto> => {
  const rs = await clientApi.put<TUpdateCompanyResDto>(`/companies/${companyId}`, payload);
  return rs.data;
};

export const deleteCompanyApi = async (companyId: string): Promise<TDeleteCompanyResDto> => {
  const rs = await clientApi.delete<TDeleteCompanyResDto>(`/companies/${companyId}`);
  return rs.data;
};
